import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from './lib/config/queryClient'
import ProtectedRoute from './components/ProtectedRoute'
import PermissionRoute from './components/PermissionRoute'
import MainLayout from './components/layout/MainLayout'
import { PERMISSIONS } from './lib/auth/usePermissions'

// Auth
import LoginPage from './features/auth/pages/LoginPage'
import SignupPage from './features/auth/pages/SignupPage'
import AcceptInvitationPage from './features/auth/pages/AcceptInvitationPage'
import { LandingPage } from './features/auth/pages/LandingPage'
import ForgotPasswordPage from './features/auth/pages/ForgotPasswordPage'
import ResetPasswordPage from './features/auth/pages/ResetPasswordPage'

// Dashboard
import DashboardPage from './features/dashboard/pages/DashboardPage'

// Projects
import ProjectsPage from './features/projects/pages/ProjectsPage'

// Test Ops
import TestCasesPage from './features/test-cases/pages/TestCasesPage'
import TestCaseDetailPage from './features/test-cases/pages/TestCaseDetailPage'
import TestPlansPage from './features/test-plans/pages/TestPlansPage'
import TestPlanDetailPage from './features/test-plans/pages/TestPlanDetailPage'
import BuildsPage from './features/test-plans/pages/BuildsPage'
import PlatformsPage from './features/test-plans/pages/PlatformsPage'
import TestSuitesPage from './features/test-suites/pages/TestSuitesPage'

// Execution Ops
import TestRunsPage from './features/test-runs/pages/TestRunsPage'
import ExecutionsPage from './features/executions/pages/ExecutionsPage'
import ExecutionDetailPage from './features/executions/pages/ExecutionDetailPage'
import RecordExecutionPage from './features/executions/pages/RecordExecutionPage'
import MyExecutionsPage from './features/executions/pages/MyExecutionsPage'
import TestResultsPage from './features/executions/pages/TestResultsPage'

// Requirements Ops
import RequirementsPage from './features/requirements/pages/RequirementsPage'
import RequirementDetailsPage from './features/requirements/pages/RequirementDetailsPage'
import ReqSpecsPage from './features/requirements/pages/ReqSpecsPage'

// Result Ops
import ReportsPage from './features/reports/pages/ReportsPage'
import AdvancedReportsPage from './features/reports/pages/AdvancedReportsPage'
import DefectsPage from './features/defects/pages/DefectsPage'
import DefectDetailsPage from './features/defects/pages/DefectDetailsPage'
import CreateDefectPage from './features/defects/pages/CreateDefectPage'

// Compliance
import CompliancePage from './features/compliance/pages/CompliancePage'
import SecurityScanningPage from './features/compliance/pages/SecurityScanningPage'

// File Ops
import FilesPage from './features/files/pages/FilesPage'
import BulkOperationsPage from './features/bulk/pages/BulkOperationsPage'

// Settings
import ProfilePage from './features/settings/pages/ProfilePage'
import TeamPage from './features/settings/pages/TeamPage'
import OrganizationPage from './features/settings/pages/OrganizationPage'
import SubscriptionsPage from './features/settings/pages/SubscriptionsPage'
import IntegrationsPage from './features/settings/pages/IntegrationsPage'

// Other
import NotificationsPage from './features/notifications/pages/NotificationsPage'

// Layouts
import TestsLayout from './features/tests/components/TestsLayout'
import ExecutionsLayout from './features/executions/components/ExecutionsLayout'
import ResultsLayout from './features/results/components/ResultsLayout'
import RequirementsOpsLayout from './features/requirements/components/RequirementsOpsLayout'
import SettingsLayout from './features/settings/components/SettingsLayout'
import FileOpsLayout from './features/files/components/FileOpsLayout'
import ComplianceLayout from './features/compliance/components/ComplianceLayout'

function LandingPageWrapper() {
    const navigate = useNavigate()
    return (
        <LandingPage
            onLogin={() => navigate('/login')}
            onSignup={() => navigate('/signup')}
        />
    )
}

function DefectDetailRedirect() {
    const { id } = useParams()
    return <Navigate to={`/results/defects/${id}`} replace />
}

export default function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <Routes>
                    {/* ── Public Routes ─────────────────────────────────────────────── */}
                    <Route path="/" element={<LandingPageWrapper />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />

                    {/* ── Dashboard ─────────────────────────────────────────────────── */}
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DashboardPage />
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />

                    {/* ── Tests Group ───────────────────────────────────────────────── */}
                    <Route
                        path="/tests/projects"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <TestsLayout>
                                        <ProjectsPage />
                                    </TestsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tests/test-cases"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <TestsLayout>
                                        <TestCasesPage />
                                    </TestsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tests/test-cases/:id"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <TestCaseDetailPage />
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tests/test-suites"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <TestsLayout>
                                        <TestSuitesPage />
                                    </TestsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tests/test-plans"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <TestsLayout>
                                        <TestPlansPage />
                                    </TestsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tests/test-plans/:id"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <TestsLayout>
                                        <TestPlanDetailPage />
                                    </TestsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tests/builds"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <TestsLayout>
                                        <BuildsPage />
                                    </TestsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tests/platforms"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <TestsLayout>
                                        <PlatformsPage />
                                    </TestsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />

                    {/* Tests legacy redirects */}
                    <Route path="/projects" element={<Navigate to="/tests/projects" replace />} />
                    <Route path="/test-cases" element={<Navigate to="/tests/test-cases" replace />} />
                    <Route path="/test-suites" element={<Navigate to="/tests/test-suites" replace />} />
                    <Route path="/test-plans" element={<Navigate to="/tests/test-plans" replace />} />
                    <Route path="/builds" element={<Navigate to="/tests/builds" replace />} />
                    <Route path="/platforms" element={<Navigate to="/tests/platforms" replace />} />

                    {/* ── Executions Group ──────────────────────────────────────────── */}
                    <Route
                        path="/executions/test-runs"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ExecutionsLayout>
                                        <TestRunsPage />
                                    </ExecutionsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/executions/test-executions"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ExecutionsLayout>
                                        <ExecutionsPage />
                                    </ExecutionsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/executions/test-executions/new"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <RecordExecutionPage />
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/executions/test-executions/:id"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ExecutionDetailPage />
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/executions/my-executions"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ExecutionsLayout>
                                        <MyExecutionsPage />
                                    </ExecutionsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />

                    {/* Executions legacy redirects */}
                    <Route path="/test-runs" element={<Navigate to="/executions/test-runs" replace />} />
                    <Route path="/executions" element={<Navigate to="/executions/test-executions" replace />} />
                    <Route path="/executions/new" element={<Navigate to="/executions/test-executions/new" replace />} />
                    <Route path="/my-executions" element={<Navigate to="/executions/my-executions" replace />} />

                    {/* ── Results Group ─────────────────────────────────────────────── */}
                    <Route
                        path="/results/test-results"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ResultsLayout>
                                        <TestResultsPage />
                                    </ResultsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/results/reports"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ResultsLayout>
                                        <ReportsPage />
                                    </ResultsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/results/advanced-reports"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ResultsLayout>
                                        <AdvancedReportsPage />
                                    </ResultsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/results/defects"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ResultsLayout>
                                        <DefectsPage />
                                    </ResultsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/results/defects/new"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <CreateDefectPage />
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/results/defects/:id"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DefectDetailsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />

                    {/* Results legacy redirects */}
                    <Route path="/test-results" element={<Navigate to="/results/test-results" replace />} />
                    <Route path="/reports" element={<Navigate to="/results/reports" replace />} />
                    <Route path="/defects" element={<Navigate to="/results/defects" replace />} />
                    <Route path="/defects/new" element={<Navigate to="/results/defects/new" replace />} />
                    <Route path="/defects/:id" element={<DefectDetailRedirect />} />

                    {/* ── Requirements Ops Group ────────────────────────────────────── */}
                    <Route
                        path="/requirements-ops/req-specs"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <RequirementsOpsLayout>
                                        <ReqSpecsPage />
                                    </RequirementsOpsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/requirements-ops/requirements"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <RequirementsOpsLayout>
                                        <RequirementsPage />
                                    </RequirementsOpsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/requirements-ops/requirements/:id"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <RequirementDetailsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />

                    {/* Requirements legacy redirects */}
                    <Route path="/req-specs" element={<Navigate to="/requirements-ops/req-specs" replace />} />
                    <Route path="/requirements" element={<Navigate to="/requirements-ops/requirements" replace />} />

                    {/* ── Compliance Group ──────────────────────────────────────────── */}
                    <Route
                        path="/compliance/configure-audit"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ComplianceLayout>
                                        <CompliancePage />
                                    </ComplianceLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/compliance/security-scanning"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ComplianceLayout>
                                        <SecurityScanningPage />
                                    </ComplianceLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/compliance" element={<Navigate to="/compliance/configure-audit" replace />} />

                    {/* ── File Ops Group ────────────────────────────────────────────── */}
                    <Route
                        path="/file-ops/files"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <FileOpsLayout>
                                        <FilesPage />
                                    </FileOpsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/file-ops/bulk-operations"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <FileOpsLayout>
                                        <BulkOperationsPage />
                                    </FileOpsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/files" element={<Navigate to="/file-ops/files" replace />} />
                    <Route path="/bulk-operations" element={<Navigate to="/file-ops/bulk-operations" replace />} />

                    {/* ── Notifications ─────────────────────────────────────────────── */}
                    <Route
                        path="/notifications"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <NotificationsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />

                    {/* ── Settings Group ────────────────────────────────────────────── */}
                    <Route
                        path="/settings/profile"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <SettingsLayout>
                                        <ProfilePage />
                                    </SettingsLayout>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings/team"
                        element={
                            <ProtectedRoute>
                                <PermissionRoute permission={PERMISSIONS.TEAM_VIEW}>
                                    <MainLayout>
                                        <SettingsLayout>
                                            <TeamPage />
                                        </SettingsLayout>
                                    </MainLayout>
                                </PermissionRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings/organization"
                        element={
                            <ProtectedRoute>
                                <PermissionRoute permission={PERMISSIONS.ORGANIZATION_VIEW}>
                                    <MainLayout>
                                        <SettingsLayout>
                                            <OrganizationPage />
                                        </SettingsLayout>
                                    </MainLayout>
                                </PermissionRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings/subscriptions"
                        element={
                            <ProtectedRoute>
                                <PermissionRoute permission={PERMISSIONS.BILLING_VIEW}>
                                    <MainLayout>
                                        <SettingsLayout>
                                            <SubscriptionsPage />
                                        </SettingsLayout>
                                    </MainLayout>
                                </PermissionRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings/integrations"
                        element={
                            <ProtectedRoute>
                                <PermissionRoute permission={PERMISSIONS.INTEGRATIONS_VIEW}>
                                    <MainLayout>
                                        <SettingsLayout>
                                            <IntegrationsPage />
                                        </SettingsLayout>
                                    </MainLayout>
                                </PermissionRoute>
                            </ProtectedRoute>
                        }
                    />

                    {/* ── Catch-all ─────────────────────────────────────────────────── */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    )
}